import React, { useEffect, useMemo, useState } from "react";
import {
  getSmsBalance,
  getSmsSubscriptions,
  buySmsSubscription,
  approveSmsSubscription,
  rejectSmsSubscription,
} from "../../services/api";
import { getCurrentUser } from "../../services/auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./sms.css";

const SMS_PRICE = 30;

export default function SmsSubscriptionPage() {
  const user = getCurrentUser();
  const isSystemAdmin = user?.role === "SYSTEM_ADMIN";
  const isParokiaAdmin = user?.role === "PAROKIA_ADMIN";

  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(false);
  const [actioningId, setActioningId] = useState(null);

  const [smsBalance, setSmsBalance] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);

  const [form, setForm] = useState({
    amount: "",
    notes: "",
  });
  const calculatedSmsCount =
    Number(form.amount || 0) > 0
      ? Math.floor(
          Number(form.amount) /
          SMS_PRICE
        )
      : 0;

  const [filters, setFilters] = useState({
    status: "ALL",
    search: "",
    fromDate: "",
    toDate: "",
  });

  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [balanceRes, subsRes] = await Promise.all([
        getSmsBalance(),
        getSmsSubscriptions(),
      ]);

      setSmsBalance(Number(balanceRes?.data?.balance || 0));
      setSubscriptions(Array.isArray(subsRes?.data) ? subsRes.data : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load SMS subscription data");
    } finally {
      setLoading(false);
    }
  };

  const handleBuySms = async () => {
  const amount = Number(form.amount || 0);
        if (!amount) {
          toast.error("Enter amount");
          return;
        }
        if (calculatedSmsCount < 100) {
          toast.error(
            "Minimum purchase is 100 SMS (TSh 3,000)"
          );
          return;
        }

    try {
      setBuying(true);

      const res = await buySmsSubscription({
        smsCount: calculatedSmsCount,
        notes: form.notes || null,
      });

      toast.success(res?.data?.message || "Request submitted for approval");

      setForm({
        amount: "",
        notes: "",
      });

      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to submit request");
    } finally {
      setBuying(false);
    }
  };

  const handleApprove = async (subscriptionId) => {
    try {
      setActioningId(subscriptionId);
      const res = await approveSmsSubscription(subscriptionId);
      toast.success(res?.data?.message || "Approved successfully");
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to approve request");
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal?.subscription_id) return;

    try {
      setActioningId(rejectModal.subscription_id);
      const res = await rejectSmsSubscription(rejectModal.subscription_id, {
        rejectionReason: rejectReason || null,
      });

      toast.success(res?.data?.message || "Rejected successfully");
      setRejectModal(null);
      setRejectReason("");
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to reject request");
    } finally {
      setActioningId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-GB");
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("en-GB");
  };

  const lowBalance = smsBalance <= 10;

  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((row) => {
      const q = filters.search.trim().toLowerCase();

      const statusOk =
        filters.status === "ALL" ? true : row.status === filters.status;

      const createdAt = row.created_at ? new Date(row.created_at) : null;
      const fromOk = !filters.fromDate
        ? true
        : createdAt
        ? createdAt >= new Date(`${filters.fromDate}T00:00:00`)
        : false;

      const toOk = !filters.toDate
        ? true
        : createdAt
        ? createdAt <= new Date(`${filters.toDate}T23:59:59`)
        : false;

      const searchOk = !q
        ? true
        : [
            row.parokia_name,
            row.notes,
            row.status,
            row.requested_by_username,
            row.approved_by_username,
            row.rejection_reason,
            row.sms_count,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));

      return statusOk && fromOk && toOk && searchOk;
    });
  }, [subscriptions, filters]);

  const latestRow = subscriptions[0] || {};
  const latestStatus = latestRow?.status || "-";
  const lastPurchaseCount = Number(latestRow?.sms_count || 0);
  const lastPurchaseDate = latestRow?.created_at || "";

  const summary = useMemo(() => {
    const totalRequests = subscriptions.length;
    const pending = subscriptions.filter((s) => s.status === "PENDING").length;
    const approved = subscriptions.filter((s) => s.status === "APPROVED").length;
    const rejected = subscriptions.filter((s) => s.status === "REJECTED").length;
    const totalRequestedSms = subscriptions.reduce(
      (sum, s) => sum + Number(s.sms_count || 0),
      0
    );

    return {
      totalRequests,
      pending,
      approved,
      rejected,
      totalRequestedSms,
    };
  }, [subscriptions]);

  const monthlyTrend = useMemo(() => {
    const months = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months.push({
        key,
        label: d.toLocaleString("en-GB", { month: "short", year: "2-digit" }),
        requested: 0,
        approved: 0,
        rejected: 0,
      });
    }

    subscriptions.forEach((row) => {
      if (!row.created_at) return;
      const d = new Date(row.created_at);
      if (Number.isNaN(d.getTime())) return;

      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const item = months.find((m) => m.key === key);
      if (!item) return;

      item.requested += Number(row.sms_count || 0);

      if (row.status === "APPROVED") {
        item.approved += Number(row.sms_count || 0);
      }

      if (row.status === "REJECTED") {
        item.rejected += Number(row.sms_count || 0);
      }
    });

    const maxValue = Math.max(
      ...months.flatMap((m) => [m.requested, m.approved, m.rejected]),
      1
    );

    return { months, maxValue };
  }, [subscriptions]);

  const statusBreakdown = useMemo(() => {
    const total = subscriptions.length || 1;
    return [
      {
        label: "Pending",
        count: summary.pending,
        percentage: Math.round((summary.pending / total) * 100),
        className: "pending",
      },
      {
        label: "Approved",
        count: summary.approved,
        percentage: Math.round((summary.approved / total) * 100),
        className: "approved",
      },
      {
        label: "Rejected",
        count: summary.rejected,
        percentage: Math.round((summary.rejected / total) * 100),
        className: "rejected",
      },
    ];
  }, [subscriptions, summary]);

  const exportCsv = () => {
    const rows = filteredSubscriptions.map((row, index) => ({
      No: index + 1,
      Parokia: row.parokia_name || "",
      SMS_Count: row.sms_count || 0,
      Balance: row.sms_balance || 0,
      Status: row.status || "",
      Notes: row.notes || "",
      Requested_By: row.requested_by_username || "",
      Approved_By: row.approved_by_username || "",
      Rejection_Reason: row.rejection_reason || "",
      Created_At: formatDateTime(row.created_at),
      Approved_At: formatDateTime(row.approved_at),
    }));

    if (rows.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `sms_subscription_audit_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success("CSV exported successfully");
  };

  return (
    <div className="sms-page">
      <div className="sms-header-card sms-header-card-dark">
        <div>
          <h1 className="sms-page-heading">SMS Subscription</h1>
          <p className="sms-page-subtitle">
            {isSystemAdmin
              ? "Manage, approve and audit SMS subscription requests"
              : "Request SMS packages and monitor your subscription history"}
          </p>
        </div>

        <div className="sms-balance-panel sms-balance-panel-dark">
          <div className="sms-balance-title">Current Balance</div>
          <div className="sms-balance-value">
            {loading ? "Loading..." : smsBalance.toLocaleString()}
          </div>
          {lowBalance && (
            <div className="sms-balance-warning">
              Low balance. Please request more SMS.
            </div>
          )}
        </div>
      </div>

      <div className="sms-tabs-dark">
        <button
          className={activeTab === "overview" ? "active" : ""}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>

        <button
          className={activeTab === "requests" ? "active" : ""}
          onClick={() => setActiveTab("requests")}
        >
          {isSystemAdmin ? "Requests / Approvals" : "My Requests"}
        </button>
      </div>

      {activeTab === "overview" && (
        <>
          <div className="sms-stats-grid">
            <div className="sms-stat-card dark">
              <div className="sms-stat-label">Total Requests</div>
              <div className="sms-stat-value">{summary.totalRequests}</div>
            </div>

            <div className="sms-stat-card dark">
              <div className="sms-stat-label">Pending</div>
              <div className="sms-stat-value">{summary.pending}</div>
            </div>

            <div className="sms-stat-card dark">
              <div className="sms-stat-label">Approved</div>
              <div className="sms-stat-value">{summary.approved}</div>
            </div>

            <div className="sms-stat-card dark">
              <div className="sms-stat-label">Rejected</div>
              <div className="sms-stat-value">{summary.rejected}</div>
            </div>

            <div className="sms-stat-card dark">
              <div className="sms-stat-label">Requested SMS Total</div>
              <div className="sms-stat-value">
                {summary.totalRequestedSms.toLocaleString()}
              </div>
            </div>

            <div className="sms-stat-card dark">
              <div className="sms-stat-label">Latest Status</div>
              <div className="sms-stat-value">{latestStatus}</div>
            </div>
          </div>

          <div className="sms-card sms-card-dark">
            <div className="sms-card-title">Subscription Summary</div>

            <div className="sms-summary-inline">
              <span>
                <strong>Balance:</strong> {loading ? "Loading..." : smsBalance}
              </span>
              <span>
                <strong>Last Buy:</strong> {lastPurchaseCount}
              </span>
              <span>
                <strong>Date:</strong> {formatDate(lastPurchaseDate)}
              </span>
              <span>
                <strong>Latest Status:</strong> {latestStatus}
              </span>
            </div>
          </div>

          {isParokiaAdmin && (
            <div className="sms-card sms-card-dark">
              <div className="sms-card-title">Request SMS Package</div>

              <div className="sms-form-grid single-grid">
                <div className="sms-column">
<div className="sms-package-row">

  <label className="sms-label required">
    SMS Package
  </label>
   <div></div>
   <div></div>
   <div></div>
   <div></div>
  <input
    type="number"
    min="3000"
    step="1000"
    placeholder="Min Tsh 3000"
    className="sms-input sms-package-input"
    value={form.amount}
    onChange={(e) =>
      setForm((prev) => ({
        ...prev,
        amount: e.target.value,
      }))
    }
  />

  <span>
    <strong>Amount:</strong>
    {" "}
    TSh {Number(form.amount || 0).toLocaleString()}
    <strong>
      
    </strong>
  </span>

  <span>
    <strong>SMS Price:</strong>
    {" "}
    TSh 30
  </span>

  <span>
    <strong>SMS Count:</strong>
    {" "}
    {calculatedSmsCount.toLocaleString()}
  </span>

</div>

                  <div className="sms-row">
                    <label className="sms-label">Notes</label>
                    <input
                      className="sms-input"
                      placeholder="Optional notes"
                      value={form.notes}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, notes: e.target.value }))
                      }
                    />
                  </div>

                  <div className="sms-row no-border">
                    <div />
                    <button
                      className="sms-action-btn" 
                      onClick={handleBuySms}
                      disabled={buying}
                    >
                      {buying ? "Submitting..." : "Submit Request"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="sms-chart-grid">
            <div className="sms-card sms-card-dark">
              <div className="sms-card-title">Approval Trend (Last 6 Months)</div>

              <div className="trend-chart">
                {monthlyTrend.months.map((month) => (
                  <div key={month.key} className="trend-chart-row">
                    <div className="trend-chart-label">{month.label}</div>

                    <div className="trend-chart-bars">
                      <div className="trend-group">
                        <span className="trend-legend">Req</span>
                        <div className="trend-bar-wrap">
                          <div
                            className="trend-bar requested"
                            style={{
                              width: `${(month.requested / monthlyTrend.maxValue) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="trend-value">{month.requested}</span>
                      </div>

                      <div className="trend-group">
                        <span className="trend-legend">App</span>
                        <div className="trend-bar-wrap">
                          <div
                            className="trend-bar approved"
                            style={{
                              width: `${(month.approved / monthlyTrend.maxValue) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="trend-value">{month.approved}</span>
                      </div>

                      <div className="trend-group">
                        <span className="trend-legend">Rej</span>
                        <div className="trend-bar-wrap">
                          <div
                            className="trend-bar rejected"
                            style={{
                              width: `${(month.rejected / monthlyTrend.maxValue) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="trend-value">{month.rejected}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sms-card sms-card-dark">
              <div className="sms-card-title">Status Breakdown</div>

              <div className="status-breakdown">
                {statusBreakdown.map((item) => (
                  <div key={item.label} className="status-breakdown-item">
                    <div className="status-breakdown-head">
                      <span>{item.label}</span>
                      <span>
                        {item.count} ({item.percentage}%)
                      </span>
                    </div>
                    <div className="status-progress">
                      <div
                        className={`status-progress-fill ${item.className}`}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === "requests" && (
        <div className="sms-card sms-card-dark">
          <div className="sms-requests-header">
            <div className="sms-card-title">
              {isSystemAdmin ? "All Requests / Approvals" : "My Requests"}
            </div>

            <div className="sms-request-actions">
              <button className="sms-action-btn dark small" onClick={exportCsv}>
                Export CSV
              </button>
            </div>
          </div>

          <div className="sms-filter-grid">
            <select
              className="sms-input"
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <input
              className="sms-input"
              placeholder="Search by parokia, notes, user..."
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search: e.target.value }))
              }
            />

            <input
              type="date"
              className="sms-input"
              value={filters.fromDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
              }
            />

            <input
              type="date"
              className="sms-input"
              value={filters.toDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
              }
            />
          </div>

          <div className="sms-table-wrap">
            <table className="sms-table sms-table-dark">
              <thead>
                <tr>
                  <th>#</th>
                  {isSystemAdmin && <th>Parokia</th>}
                  <th>SMS Count</th>
                  <th>Amount (TSh)</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Requested By</th>
                  <th>Approved By</th>
                  <th>Request Date</th>
                  <th>Approval Date</th>
                  <th>Reason</th>
                  {isSystemAdmin && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={isSystemAdmin ? "11" : "10"}
                      className="sms-empty"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : filteredSubscriptions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isSystemAdmin ? "11" : "10"}
                      className="sms-empty"
                    >
                      No requests found
                    </td>
                  </tr>
                ) : (
                  filteredSubscriptions.map((row, index) => (
                    <tr key={row.subscription_id || index}>
                      <td>{index + 1}</td>
                      {isSystemAdmin && <td>{row.parokia_name || "-"}</td>}
                      <td>{Number(row.sms_count || 0).toLocaleString()}</td>
                      <td>{Number(row.amount_paid || 0).toLocaleString()}</td>
                      <td>{Number(row.sms_balance || 0).toLocaleString()}</td>
                      <td>
                        <span className={`sms-status-badge ${String(row.status || "").toLowerCase()}`}>
                          {row.status || "-"}
                        </span>
                      </td>
                      <td>{row.notes || "-"}</td>
                      <td>{row.requested_by_username || "-"}</td>
                      <td>{row.approved_by_username || "-"}</td>
                      <td>{formatDateTime(row.created_at)}</td>
                      <td>{formatDateTime(row.approved_at)}</td>
                      <td>{row.rejection_reason || "-"}</td>

                      {isSystemAdmin && (
                        <td>
                          {row.status === "PENDING" ? (
                            <div className="sms-table-actions">
                              <button
                                className="sms-mini-btn approve"
                                onClick={() => handleApprove(row.subscription_id)}
                                disabled={actioningId === row.subscription_id}
                              >
                                {actioningId === row.subscription_id
                                  ? "..."
                                  : "Approve"}
                              </button>

                              <button
                                className="sms-mini-btn reject"
                                onClick={() => setRejectModal(row)}
                                disabled={actioningId === row.subscription_id}
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className="sms-inline-muted">Done</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal sms-modal-dark">
            <h3>Reject SMS Subscription</h3>

            <div className="sms-modal-context">
              <div><strong>Parokia:</strong> {rejectModal.parokia_name || "-"}</div>
              <div><strong>SMS Count:</strong> {rejectModal.sms_count || "-"}</div>
            </div>

            <textarea
              className="sms-input"
              style={{ minHeight: 110 }}
              placeholder="Enter rejection reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />

            <div className="modal-actions">
              <button
                className="sms-action-btn light"
                onClick={() => {
                  setRejectModal(null);
                  setRejectReason("");
                }}
              >
                Cancel
              </button>

              <button
                className="sms-action-btn danger"
                onClick={handleReject}
                disabled={actioningId === rejectModal.subscription_id}
              >
                {actioningId === rejectModal.subscription_id
                  ? "Rejecting..."
                  : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}